// Comply · Dashboard — compliance command centre.
//
// Thin renderer over the server-composed `/api/comply/dashboard` payload
// (KPIs, next regulatory deadline, alerts feed, attention items). Every figure
// is real ontology data read through `ctx.read` server-side; there is no
// fabricated fallback (ADR-0005). When a source is empty the relevant card
// renders an honest empty state rather than sample rows.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ComplyAlertFeedItem, ComplyDashboardResponse } from '@septima/contract';
import { PageHeader, Card, Stat, Pill, DataTable } from '@/components/ui';
import type { DataTableColumn, PillVariant, StatTone } from '@/components/ui';
import EmptyState from '@/components/EmptyState';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Rag = 'Green' | 'Amber' | 'Red';

function ragTone(rag: Rag): StatTone {
    switch (rag) {
        case 'Green':
            return 'positive';
        case 'Amber':
            return 'warning';
        case 'Red':
            return 'negative';
    }
}

function severityVariant(severity: string): PillVariant {
    switch (severity) {
        case 'High':
        case 'Material':
            return 'red';
        case 'Medium':
        case 'Potential material':
            return 'amber';
        default:
            return 'grey';
    }
}

const ALERT_COLUMNS: DataTableColumn<ComplyAlertFeedItem>[] = [
    {
        id: 'date',
        header: 'Date',
        numeric: true,
        minWidth: '110px',
        sortable: true,
        sortValue: (a) => a.date,
        cell: (a) => <span className="text-[rgb(var(--color-text-secondary))]">{a.date}</span>,
    },
    {
        id: 'type',
        header: 'Type',
        minWidth: '140px',
        sortable: true,
        sortValue: (a) => a.type,
        cell: (a) => <span className="text-[rgb(var(--color-text-primary))]">{a.type}</span>,
    },
    {
        id: 'client',
        header: 'Client',
        minWidth: '160px',
        sortable: true,
        sortValue: (a) => a.client.toLowerCase(),
        cell: (a) => <span className="text-[rgb(var(--color-text-secondary))]">{a.client}</span>,
    },
    {
        id: 'description',
        header: 'Description',
        minWidth: '240px',
        cell: (a) => (
            <span className="text-[rgb(var(--color-text-secondary))]">{a.description}</span>
        ),
    },
    {
        id: 'severity',
        header: 'Severity',
        align: 'center',
        minWidth: '110px',
        sortable: true,
        sortValue: (a) => a.severity,
        cell: (a) => <Pill variant={severityVariant(a.severity)} label={a.severity} size="sm" />,
    },
    {
        id: 'status',
        header: 'Status',
        align: 'center',
        minWidth: '130px',
        cell: (a) => <Pill variant="grey" label={a.status} size="sm" />,
    },
];

export default function ComplyDashboardPage() {
    const { data, isLoading } = useQuery<ComplyDashboardResponse>({
        queryKey: ['comply', 'dashboard'],
        queryFn: async () => {
            const res = await api.complyDashboard.get();
            if (res.status !== 200) throw new Error(`comply-dashboard fetch failed: ${res.status}`);
            return res.body;
        },
        refetchInterval: 30_000,
    });

    const kpis = data?.kpis;
    const openCount = data?.openCount ?? 0;
    const nextDeadline = data?.nextDeadline ?? null;
    const alertsFeed = useMemo<ComplyAlertFeedItem[]>(() => data?.alertsFeed ?? [], [data]);
    const attentionItems = useMemo(() => data?.attentionItems ?? [], [data]);

    return (
        <div className="flex flex-col h-full">
            <PageHeader
                title="Dashboard"
                subtitle="Live view of breaches, reviews, capital posture and upcoming returns."
            />

            <div className="flex-1 overflow-y-auto">
                <div className="max-w-content mx-auto flex flex-col gap-section-gap px-content-padding pt-7 pb-bottom-padding">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-card-gap">
                        <Stat
                            label="Open breaches"
                            value={kpis ? kpis.openBreaches.toString() : '—'}
                            detail={
                                kpis
                                    ? `${kpis.materialOpen} material, ${kpis.nonMaterialOpen} non-material`
                                    : undefined
                            }
                            tone={kpis ? ragTone(kpis.breachRAG) : 'neutral'}
                        />
                        <Stat
                            label="Overdue reviews"
                            value={kpis ? kpis.totalOverdue.toString() : '—'}
                            detail={
                                kpis ? `${kpis.overdueKYC} KYC, ${kpis.overdueCMP} CMP` : undefined
                            }
                            tone={kpis ? ragTone(kpis.overdueRAG) : 'neutral'}
                        />
                        <Stat
                            label="Consumer Duty"
                            value={
                                kpis && kpis.avgConsumerDuty > 0 ? `${kpis.avgConsumerDuty}%` : '—'
                            }
                            detail="Firm average across four pillars"
                            tone={
                                kpis && kpis.avgConsumerDuty > 0
                                    ? ragTone(kpis.consumerDutyRAG)
                                    : 'neutral'
                            }
                        />
                        <Stat
                            label="Capital ratio"
                            value={
                                kpis && kpis.capitalRatio > 0
                                    ? `${kpis.capitalRatio.toFixed(2)}x`
                                    : '—'
                            }
                            detail="Own funds against requirement"
                            tone={
                                kpis && kpis.capitalRatio > 0 ? ragTone(kpis.capitalRAG) : 'neutral'
                            }
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-card-gap">
                        <div className="lg:col-span-2">
                            <Card
                                title="Exception feed"
                                actions={
                                    <Pill
                                        variant={alertsFeed.length > 0 ? 'amber' : 'green'}
                                        label={
                                            alertsFeed.length > 0
                                                ? `${alertsFeed.length} open`
                                                : 'Clear'
                                        }
                                        size="sm"
                                    />
                                }
                                noPadding
                            >
                                <DataTable<ComplyAlertFeedItem>
                                    ariaLabel="Exception feed"
                                    columns={ALERT_COLUMNS}
                                    data={alertsFeed}
                                    rowKey={(a) => a.id}
                                    loading={isLoading}
                                    initialSort={{ id: 'date', desc: true }}
                                    empty={
                                        <EmptyState
                                            title="No active alerts"
                                            body="Screening and suitability alerts surface here as compliance sources are ingested."
                                        />
                                    }
                                />
                            </Card>
                        </div>

                        <Card title={nextDeadline?.name ?? 'Next deadline'}>
                            {nextDeadline ? (
                                <div className="flex flex-col gap-3">
                                    <span className="text-secondary-metric font-mono tabular-nums text-[rgb(var(--color-text-primary))]">
                                        {nextDeadline.dueDate}
                                    </span>
                                    <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                                        {nextDeadline.authority} · {nextDeadline.period}
                                        {nextDeadline.preparer
                                            ? ` · Preparer: ${nextDeadline.preparer}`
                                            : ''}
                                    </p>
                                    <Pill variant="grey" label={nextDeadline.status} size="sm" />
                                </div>
                            ) : (
                                <EmptyState
                                    title="Regulatory calendar clear"
                                    body="No imminent return is outstanding."
                                />
                            )}
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 gap-card-gap">
                        <Card
                            title="Attention required"
                            actions={
                                <Pill
                                    variant={openCount > 0 ? 'amber' : 'green'}
                                    label={openCount > 0 ? `${openCount} open` : 'No open items'}
                                    size="sm"
                                />
                            }
                        >
                            {attentionItems.length > 0 ? (
                                <ul className="flex flex-col">
                                    {attentionItems.map((item) => (
                                        <li
                                            key={item.id}
                                            className="flex items-center gap-3 py-2.5 border-b border-[rgb(var(--color-divider)/0.6)] last:border-b-0"
                                        >
                                            <span
                                                className={cn(
                                                    'h-2 w-2 flex-none rounded-full',
                                                    item.ragVariant === 'red'
                                                        ? 'bg-[rgb(var(--color-rag-red))]'
                                                        : item.ragVariant === 'amber'
                                                          ? 'bg-[rgb(var(--color-rag-amber))]'
                                                          : 'bg-[rgb(var(--color-rag-green))]',
                                                )}
                                            />
                                            <span className="min-w-0 flex-1 truncate text-table-body text-[rgb(var(--color-text-primary))]">
                                                {item.label}
                                            </span>
                                            <span className="flex-none text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                                                {item.age}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <EmptyState
                                    title="Nothing requires attention"
                                    body="Material breaches and action-required suitability alerts list here when raised."
                                />
                            )}
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
